/**
 * OverlayScrollbar — a thin scrollbar that FLOATS over a scroll container's
 * right margin instead of reserving layout width like a native scrollbar (which
 * shrinks the content box). The target's native scrollbar is hidden via CSS
 * (`.overlay-scroll`), the content keeps its full width, and this draggable
 * accent pill is drawn on top of the existing right-hand margin.
 *
 * Attach the same ref to the scroll element and to this component:
 *   <div ref={ref} className="overlay-scroll" style={{ overflowY: 'auto' }}>…</div>
 *   <OverlayScrollbar targetRef={ref} />
 *
 * Rendered through a portal with fixed positioning (like ScrollArrows), so it
 * needs no DOM restructuring and recomputes on scroll / resize / DOM mutations.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

interface Props {
  targetRef: React.RefObject<HTMLElement | null>;
  /** Distance (px) from the target's right edge to the thumb's CENTER — tune so
   *  the pill sits the right distance from whichever edge `side` picks. */
  rightInset?: number;
  /** Which edge the bar floats against. Default 'right'. */
  side?: 'left' | 'right';
  /** Inset (px) from the top and bottom of the track. */
  vInset?: number;
  /** Minimum overflow (px) before the bar appears. */
  threshold?: number;
}

interface Metrics { show: boolean; left: number; top: number; height: number; }
const HIDDEN: Metrics = { show: false, left: 0, top: 0, height: 0 };
const MIN_THUMB = 32;

/** Read a numeric CSS custom property off <html>, falling back to `def`. */
function cssNum(name: string, def: number): number {
  if (typeof document === 'undefined') return def;
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : def;
}

export default function OverlayScrollbar({ targetRef, rightInset = 10, side = 'right', vInset = 14, threshold = 8 }: Props) {
  const [m, setM] = useState<Metrics>(HIDDEN);
  const rafRef = useRef<number | undefined>(undefined);
  const drag = useRef<{ startY: number; startScroll: number } | null>(null);

  const measure = useCallback(() => {
    rafRef.current = undefined;
    const el = targetRef.current;
    if (!el) return;
    const overflow = el.scrollHeight - el.clientHeight;
    if (overflow <= threshold) { setM(HIDDEN); return; }
    const r = el.getBoundingClientRect();
    // Tunable, shared across editors: --scrollbar-y scales the track vertically
    // (top/bottom inset), --scrollbar-x is the distance from the edge.
    const vi = cssNum('--scrollbar-y', vInset);
    const inset = cssNum('--scrollbar-x', rightInset);
    const trackH = r.height - vi * 2;
    const thumbH = Math.max(MIN_THUMB, (el.clientHeight / el.scrollHeight) * trackH);
    const maxThumbTop = Math.max(0, trackH - thumbH);
    const top = r.top + vi + (el.scrollTop / overflow) * maxThumbTop;
    const left = side === 'left' ? r.left + inset : r.right - inset;
    setM({ show: true, left, top, height: thumbH });
  }, [targetRef, rightInset, side, vInset, threshold]);

  // rAF-debounce a burst of events into one measurement per frame.
  const schedule = useCallback(() => {
    if (rafRef.current == null) rafRef.current = requestAnimationFrame(measure);
  }, [measure]);

  useEffect(() => {
    const el = targetRef.current;
    if (!el) return;
    schedule();
    el.addEventListener('scroll', schedule, { passive: true });
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    const mo = new MutationObserver(schedule);
    mo.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
    window.addEventListener('resize', schedule);
    return () => {
      // Reset the handle (not just cancel) so a StrictMode re-mount reschedules.
      if (rafRef.current != null) { cancelAnimationFrame(rafRef.current); rafRef.current = undefined; }
      el.removeEventListener('scroll', schedule);
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [targetRef, schedule]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    const el = targetRef.current;
    if (!el) return;
    drag.current = { startY: e.clientY, startScroll: el.scrollTop };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    e.preventDefault();
    e.stopPropagation();
  }, [targetRef]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const el = targetRef.current;
    if (!el || !drag.current) return;
    const r = el.getBoundingClientRect();
    const overflow = el.scrollHeight - el.clientHeight;
    const vi = cssNum('--scrollbar-y', vInset);
    const trackH = r.height - vi * 2;
    const thumbH = Math.max(MIN_THUMB, (el.clientHeight / el.scrollHeight) * trackH);
    const maxThumbTop = Math.max(1, trackH - thumbH);
    const dy = e.clientY - drag.current.startY;
    el.scrollTop = drag.current.startScroll + (dy / maxThumbTop) * overflow;
  }, [targetRef, vInset]);

  const onPointerUp = useCallback(() => { drag.current = null; }, []);

  if (!m.show) return null;

  return ReactDOM.createPortal(
    <div
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        position: 'fixed',
        left: m.left,
        top: m.top,
        height: m.height,
        width: 4,
        transform: 'translateX(-50%)',
        background: 'oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.9)',
        borderRadius: 999,
        cursor: 'grab',
        zIndex: 40,
        pointerEvents: 'auto',
        boxShadow: '0 0 10px oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.4)',
      }}
    />,
    document.body,
  );
}
